import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from '@base-ui-components/react/dialog';
import { Button } from './Button';
import { cn } from './baseUIConfig';

export const ModalContext = React.createContext(null);

// This dialog renders through a portal on <body>, so its z-index competes with
// every full-screen page overlay in the app rather than with its own subtree.
// It has to win: a modal the user just opened must never be painted over.
//
// The old 50/51 lost to any page-level takeover. Guided Forge is the case that
// exposed it — /play-now embeds FastCharacterCreator inside `.ncf-overlay`
// (z-index 9999, background rgba(0,0,0,0.95)), so the voice picker opened into
// the DOM at 896x648 and was covered completely, which reads to the player as
// a Voice button that does nothing.
//
// 10050 clears the app's highest overlay (10000, `.ncf-launch-overlay` and
// peers) with headroom. Anything added above this needs to be a deliberate
// "sits over an open dialog" decision, not an accident.
const MODAL_BACKDROP_Z_INDEX = 10050;
const MODAL_POPUP_Z_INDEX = 10051;

// Tailwind `max-w-*` tokens map to CSS max-width values for the inline .fc-shell sizing.
const WIDTH_TOKEN_TO_PX = {
  'max-w-sm': '24rem',
  'max-w-md': '28rem',
  'max-w-lg': '32rem',
  'max-w-xl': '36rem',
  'max-w-2xl': '42rem',
  'max-w-3xl': '48rem',
  'max-w-4xl': '56rem',
  'max-w-5xl': '64rem',
  'max-w-6xl': '72rem',
  'max-w-7xl': '80rem',
  'max-w-full': '100%',
};

export const Modal = ({
  open,
  onClose,
  title,
  children,
  width = 'max-w-2xl',
  showCloseButton = true,
  className = '',
  preventBackdropClose = false,
}) => {
  const dialogRef = useRef(null);
  const [portalContainer, setPortalContainer] = useState(null);
  const setDialogRef = useCallback((node) => {
    dialogRef.current = node;
    setPortalContainer(node);
  }, []);

  useEffect(() => {
    if (open) {
      const handleEsc = (e) => {
        if (e.key === 'Escape' && !preventBackdropClose) {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [open, onClose, preventBackdropClose]);

  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !preventBackdropClose) {
      onClose();
    }
  };

  const shellMaxWidth = WIDTH_TOKEN_TO_PX[width] ?? width;

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Backdrop
          className="fc-overlay"
          style={{ position: 'fixed', inset: 0, zIndex: MODAL_BACKDROP_Z_INDEX }}
          onClick={handleBackdropClick}
        />
        <Dialog.Popup
          ref={setDialogRef}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: MODAL_POPUP_Z_INDEX,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '36px',
            pointerEvents: 'none',
          }}
        >
          <div
            className={cn('fc-shell', className)}
            style={{
              pointerEvents: 'auto',
              width: '100%',
              height: 'auto',
              maxWidth: shellMaxWidth,
              maxHeight: '90vh',
            }}
          >
            {title && (
              <Dialog.Title
                className="flex items-center justify-between border-b border-[rgba(207,161,64,0.22)] px-6 py-5 text-2xl font-bold text-gaia-text"
                style={{ fontFamily: 'Cinzel, serif', flexShrink: 0 }}
              >
                {title}
                {showCloseButton && (
                  <Button
                    onClick={onClose}
                    variant="ghost"
                    size="sm"
                    className={cn('h-9 w-9 px-0 text-gaia-text-dim hover:text-gaia-text')}
                    aria-label="Close modal"
                  >
                    ✕
                  </Button>
                )}
              </Dialog.Title>
            )}
            <div className="flex-1 overflow-y-auto p-6">
              <ModalContext.Provider value={{ portalContainer }}>
                {children}
              </ModalContext.Provider>
            </div>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
